import {useQuery} from "@tanstack/react-query";
import {sameCurrency} from "@/features/pricing";
import {AxiosError} from "axios";
import {createProductServiceRepository} from "../data/repositories";
import {GetStoreServiceApiResponse} from "../../../react-query-hooks/store/services/get-store-service/types";
import {CreateSessionApiBookingQuestionsPayload} from "../../../react-query-hooks/session/types";
import {CoverImagesItemResponse} from "../../../react-query-hooks/products/types";
import {GET_STORE_SERVICE_RQ_KEY} from "../../../react-query-hooks/store/services/constants";

/**
 * Legacy hook for fetching a single product service.
 * Maintains backward compatibility with existing code.
 *
 * @deprecated Use useProductService from features/product-services instead.
 * This hook will be removed in a future release.
 *
 * @param storeServiceId - The product service ID to fetch
 * @returns React Query result with legacy response shape
 *
 * @example
 * // Old usage (deprecated):
 * const { data } = useGetStoreServiceApi(123);
 * const name = data?.productService.name;
 *
 * // New usage (recommended):
 * const { data: productService } = useProductService(123);
 * const name = productService?.name;
 */
export const useGetStoreServiceApi = (storeServiceId?: number) => {
  const repository = createProductServiceRepository();

  return useQuery<
    GetStoreServiceApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    // Keep the legacy single query key so the v1 update hook's invalidation
    // ([GET_STORE_SERVICE_RQ_KEY, String(serviceId)]) still refreshes the edit page.
    queryKey: [GET_STORE_SERVICE_RQ_KEY, String(storeServiceId)],
    queryFn: async (): Promise<GetStoreServiceApiResponse> => {
      const productService = await repository.getSingle(storeServiceId!);
      const props = productService.toProps();
      // The response never sends `isBase`, so the legacy shape's `is_base` — which is
      // what the edit form sorts the base row to the front by — has to be derived.
      const baseCurrency = productService.getBaseCurrency();

      // Map domain entity back to legacy response shape
      return {
        productService: {
          id: props.id,
          userId: props.userId,
          name: props.name,
          slug: props.slug,
          tiny_url: props.tinyUrl,
          description: props.description,
          delivery_timeframe_unit: props.deliveryTimeframeUnit,
          delivery_timeframe_value: props.deliveryTimeframeValue,
          hideFromProfile: props.hideFromProfile,
          url_redirect: props.urlRedirect,
          redirectType: props.redirectType,
          telegramGroupChatId: props.telegramGroupChatId,
          statusId: props.statusId,
          request_phone_number: props.requestPhoneNumber,
          purchaseLimit: props.purchaseLimit,
          booking_questions: props.bookingQuestions.map((q) => ({
            question: q.question,
            answer_type: q.answerType,
            required: q.required,
            options: q.options,
          })) as CreateSessionApiBookingQuestionsPayload[],
          bookingQuestions: props.bookingQuestions.map((q) => ({
            question: q.question,
            answer_type: q.answerType,
            required: q.required,
            options: q.options,
          })) as CreateSessionApiBookingQuestionsPayload[],
          cover_images: props.coverImages.map(
            (url, index): CoverImagesItemResponse => ({
              id: `cover-${props.id}-${index}`,
              media_url: url,
              media_type: "image",
              offering_id: props.id,
              offering_type: "product-service",
              user_id: props.userId,
              createdAt: props.createdAt,
              updatedAt: props.updatedAt,
            })
          ),
          enable_currency_conversion: props.enableCurrencyConversion,
          is_pay_what_you_want: props.isPayWhatYouWant,
          currencies: props.currencies.map((c) => ({
            id: c.id,
            userId: c.userId,
            service_id: c.serviceId,
            service_type: c.serviceType,
            currency: c.currency,
            amount: c.amount,
            min_call_price: c.minCallPrice,
            is_auto_converted: c.isAutoConverted,
            is_base: sameCurrency(c.currency, baseCurrency?.currency),
            createdAt: c.createdAt,
            updatedAt: c.updatedAt,
          })),
          createdAt: props.createdAt,
          updatedAt: props.updatedAt,
          deleted_at: null,
        },
      };
    },
    enabled: !!storeServiceId,
  });
};
